import { PortfolioEditorMode } from '@ddt-web/config';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { AnalysisState } from './analysis.state';
import { ApplicationState } from './application-state.reducer';
import { CmaState } from './cma-state';
import { PEState } from './pe-state';

export const getAppState = createFeatureSelector<ApplicationState>('applicationState');

export const getPEState = createSelector(
	getAppState,
	(state: ApplicationState) => state.peState
);

export const getIsModalVisible = (state: PEState) => state.isModalVisible;

export const getUniqueIdForPortfolioEditor = (state: PEState) => state.uniqueIdForPortfolioEditor;

export const getComparisonIdsForPortfolioEditor = (state: PEState) => state.comparisonIds;

export const getEngagementIdForPortfolioEditor = (state: PEState) => state.engagementId;

export const getAssetClassesForPortfolioEditor = (state: PEState) => state.assetClasses;

export const getStressTestsForPortfolioEditor = (state: PEState) => state.stressTests;

export const getAcBenchmarkForPortfolioEditor = (state: PEState) => state.acBenchmark;

export const getAssetTypesForPortfolioEditor = (state: PEState) => state.assetTypes;

export const getUrlIdsForPortfolioEditor = (state: PEState) => state.urlIds;

export const getPEModeForPortfolioEditor = (state: PEState) => state.portfolioEditorMode;

export const getApbState = createSelector(
	getAppState,
	(state: ApplicationState) => state.apbState
);

export const getProposalState = createSelector(
	getAppState,
	(state: ApplicationState) => state.proposalState
);

export const getPortfolioIdsState = createSelector(
	getAppState,
	(state: ApplicationState) => state.portfolioIdsState
);

export const getPerformanceState = createSelector(
	getAppState,
	(state: ApplicationState) => state.performanceState
);

export const getRiskState = createSelector(
	getAppState,
	(state: ApplicationState) => state.riskState
);

export const getGenerateReportDataState = createSelector(
	getAppState,
	(state: ApplicationState) => state.generateReportDataState
);

export const getFundsToExploreState = createSelector(
	getAppState,
	(state: ApplicationState) => state.fundsToExploreState
);

export const getSelectedPortfolioIndexState = createSelector(
	getAppState,
	(state: ApplicationState) => state.selectedPortfolioIndexState
);

export const getResumeExcelDownloadState = createSelector(
	getAppState,
	(state: ApplicationState) => state.resumeExcelDownloadState
);

export const getApplicationState = createSelector(
	getAppState,
	(state: ApplicationState) => state
);

export const getCmaState = createSelector(
	getAppState,
	(state: ApplicationState) => state.cmaState
);

export const getFeatureFlags = createSelector(
	getAppState,
	(state: ApplicationState) => state.featureFlags
);

export const getIsPEModelVisible: MemoizedSelector<object, boolean> = createSelector(
	getPEState,
	getIsModalVisible
);

export const getUniqueIdForPEModel: MemoizedSelector<object, string> = createSelector(
	getPEState,
	getUniqueIdForPortfolioEditor
);

export const getComparisonIdsForPEModel: MemoizedSelector<object, string[]> = createSelector(
	getPEState,
	getComparisonIdsForPortfolioEditor
);

export const getEngagementIdForPEModel: MemoizedSelector<object, string> = createSelector(
	getPEState,
	getEngagementIdForPortfolioEditor
);

export const getAssetClassesForPEModel: MemoizedSelector<object, string[]> = createSelector(
	getPEState,
	getAssetClassesForPortfolioEditor
);

export const getStressTestsForPEModel: MemoizedSelector<object, string[]> = createSelector(
	getPEState,
	getStressTestsForPortfolioEditor
);

export const getAcBenchmarkForPEModel: MemoizedSelector<object, string> = createSelector(
	getPEState,
	getAcBenchmarkForPortfolioEditor
);

export const getAssetTypesForPEModel: MemoizedSelector<object, { displayName: string; label?: string; count?: number }[]> = createSelector(
	getPEState,
	getAssetTypesForPortfolioEditor
);

export const getUrlIdsForPEModel: MemoizedSelector<object, string[]> = createSelector(
	getPEState,
	getUrlIdsForPortfolioEditor
);

export const getPEModeForPEModel: MemoizedSelector<object, PortfolioEditorMode> = createSelector(
	getPEState,
	getPEModeForPortfolioEditor
);

// analysis state
export const getAnalysisState = createSelector(
	getAppState,
	(state: ApplicationState) => state.analysisState
);

export const getSelectedCurrency = (state: AnalysisState) => state.selectedCurrency;
export const getSelectedBenchmark = (state: AnalysisState) => state.selectedBenchmark;
export const getIqStatusOrAof = (state: AnalysisState) => state.iqStatusOrAreaOfFocus;

export const getAnalysisStateCurrency = createSelector(
	getAnalysisState,
	getSelectedCurrency
);

export const getAnalysisStateBenchmark = createSelector(
	getAnalysisState,
	getSelectedBenchmark
);

export const getAnalysisStateIqStatusOrAoF = createSelector(
	getAnalysisState,
	getIqStatusOrAof
);

export const getIsTransientPortfolioEdited = (state: CmaState) => state.isTransientPortfolioEdited;

export const getCmaIsTransientPortfolioEdited = createSelector(
	getCmaState,
	getIsTransientPortfolioEdited
);

export const getIretireUserData = createSelector(
	getAppState,
	(state: ApplicationState) => state.iretireUserData
);

export const getAnalysedPortfoliosTaxQualifiedType = createSelector(
	getAppState,
	(state: ApplicationState) => state.taxPortfolioAnalysisType
);

export const getMeSelectedModelsData = createSelector(
	getAppState,
	(state: ApplicationState) => state.meState
);

export const getIsDismissedFundsUpdated = createSelector(
	getAppState,
	(state: ApplicationState) => state.isDismissedFundsUpdated
);

export const getImportPartnerPortfoliosState = createSelector(
	getAppState,
	(state: ApplicationState) => state.importPartnerPortfoliosState
);

export const getPartnerAuthenticationState = createSelector(
	getAppState,
	(state: ApplicationState) => state.partnerAuthenticationState
);

export const getPartnerAcknowledgementState = createSelector(
	getAppState,
	(state: ApplicationState) => state.partnerAcknowledgementState
);
